import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppShell, Skeleton, UsageMeter, UserBadge, useTheme, useToast, type SidebarSection } from '@koyomi/ui';
import { useEffect, useRef } from 'react';
import { useAttention } from '../../lib/messaging-api.ts';
import { disconnectSocket, useRealtime, useRealtimeResync } from '../../lib/socket.ts';
import { useActiveBusiness, useAuthStore } from '../../app/auth-store.ts';
import { api } from '../../lib/api.ts';
import { useBusiness, useSave } from '../../lib/queries.ts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HelpButton } from '../help/HelpButton.tsx';

/** Sidebar dos mockups: seção principal + Configuração (admin) ou Meus horários/resumo (padrão). */
export function AppLayout() {
  const { t, i18n } = useTranslation(['nav', 'common', 'auth']);
  const business = useActiveBusiness()!;
  const { user, businesses, clear, setBusiness } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  useTheme();
  const isAdmin = business.role === 'ADMIN';
  const full = useBusiness();
  const grants = useQuery({ queryKey: [full.data?.id, 'support-access'], queryFn: () => api<{ id: string; status: string; scope: string[]; reason: string; superAdminName: string; requestedDurationMin: number }[]>('/business/support-access'), enabled: !!full.data && business.role === 'ADMIN', refetchInterval: 60_000 });
  const decide = useSave((v: { id: string; approve: boolean }) => api(`/business/support-access/${v.id}/decide`, { body: { approve: v.approve } }), ['support-access']);
  const pendingGrant = (grants.data ?? []).find((g) => g.status === 'REQUESTED');
  const usage = full.data?.usage;
  const usagePct = usage?.pct ?? 0;
  // Conversas esperando uma pessoa: badge no menu, título da aba e aviso quando uma nova entra (fora da tela de Conversas).
  const attention = useAttention();
  const toast = useToast();
  const queryClient = useQueryClient();
  useRealtimeResync();
  useRealtime({
    'conversation.message': () => queryClient.invalidateQueries({ queryKey: [full.data?.id, 'attention'] }),
    'conversation.state': () => queryClient.invalidateQueries({ queryKey: [full.data?.id, 'attention'] }),
  });
  const awaiting = attention.data?.count ?? 0;
  // Wizard de primeira configuração: no primeiro acesso de um admin a um negócio novo, a Ativação abre direto no wizard.
  const wizard = full.data?.onboarding?.wizard;
  const redirected = useRef<string | null>(null);
  useEffect(() => {
    if (!isAdmin || !full.data || wizard?.status !== 'AVAILABLE' || wizard.firstSeenAt || redirected.current === full.data.id) return;
    redirected.current = full.data.id;
    if (!location.pathname.startsWith('/ativacao')) navigate('/ativacao', { replace: true });
  }, [isAdmin, full.data?.id, wizard?.status, wizard?.firstSeenAt]);
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    const list = attention.data?.conversations ?? [];
    if (!attention.data) return;
    const fresh = list.filter((c) => !seen.current.has(`${c.id}:${c.unreadCount}`));
    for (const c of list) seen.current.add(`${c.id}:${c.unreadCount}`);
    if (seen.current.size > 500) seen.current = new Set(list.map((c) => `${c.id}:${c.unreadCount}`));
    if (!fresh.length || attention.isPlaceholderData) return;
    const inThread = location.pathname.startsWith('/conversas') && fresh.every((c) => location.search.includes(c.id));
    if (inThread) return;
    const c = fresh[0]!;
    toast.push(<span>{t('nav:attentionToast', { name: c.clientName ?? '', count: fresh.length })} <NavLink to={`/conversas?c=${c.id}`}>{t('nav:main.conversations')}</NavLink></span>, 'warn');
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.visibilityState !== 'visible') { try { new Notification(t('nav:attentionTitle', { count: fresh.length }), { body: c.preview ?? c.clientName ?? '', tag: 'koyomi-attention' }); } catch { /* ignore */ } }
  }, [attention.data]);
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\) /, '');
    document.title = awaiting > 0 ? `(${awaiting}) ${base}` : base;
  }, [awaiting]);

  const item = (key: string, label: string, href: string, badge?: string) => ({ key, label, href, badge, active: location.pathname === href || location.pathname.startsWith(href + '/') });
  const main = [
    item('agenda', isAdmin ? t('nav:main.agenda') : t('nav:main.myAgenda'), '/agenda'),
    item('conversations', t('nav:main.conversations'), '/conversas', awaiting > 0 ? String(awaiting) : undefined),
    item('waitlist', t('nav:main.waitlist'), '/fila'),
    item('clients', t('nav:main.clients'), '/clientes'),
  ];
  if (isAdmin) main.push(item('reports', t('nav:main.reports'), '/relatorios'), item('audit', t('nav:main.audit'), '/auditoria'));
  const config = isAdmin
    ? [
        item('business', t('nav:config.business'), '/config/negocio'),
        item('ai', t('nav:config.ai'), '/config/ia'),
        item('services', t('nav:config.services'), '/config/servicos'),
        item('resources', t('nav:config.resources'), '/config/recursos'),
        item('hours', t('nav:config.hours'), '/config/horarios'),
        item('team', t('nav:config.team'), '/config/equipe'),
        ...(full.data && full.data.modality !== 'LOCAL' ? [item('home', t('nav:config.homeService'), '/config/domicilio')] : []),
        item('messages', t('nav:config.messages'), '/config/mensagens'),
        item('whatsapp', t('nav:config.whatsapp'), '/config/whatsapp'),
        item('plan', t('nav:config.plan'), '/config/plano'),
        item('onboarding', t('nav:config.onboarding'), '/ativacao'),
        item('test', t('nav:config.testMode'), '/config/teste'),
      ]
    : [item('myHours', t('nav:config.myHours'), '/config/meus-horarios'), item('mySummary', t('nav:config.mySummary'), '/config/meu-resumo')];
  const sections: SidebarSection[] = [{ items: main }, { label: t('nav:configSection'), items: config }, { items: [item('help', t('nav:help'), '/ajuda')] }];

  const logout = async () => {
    await api('/auth/logout', { method: 'POST', global: true }).catch(() => undefined);
    disconnectSocket();
    clear();
    queryClient.clear();
  };
  const switchBusiness = () => {
    if (businesses.length <= 1) return;
    disconnectSocket();
    queryClient.clear();
    setBusiness(null);
    navigate('/select-business');
  };
  const initials = (user?.name ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const month = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(new Date()).replace('.', '');

  return (
    <AppShell
      brand={{ initial: business.name[0]?.toUpperCase() ?? 'K', title: business.name, subtitle: business.segment ?? undefined, onClick: switchBusiness }}
      sections={sections}
      renderLink={(it, className, children) => <NavLink key={it.key} to={it.href} className={className}>{children}</NavLink>}
      footer={
        <>
          {full.isLoading ? <Skeleton lines={2} height={10} /> : <UsageMeter label={t('nav:usage', { month })} pct={usagePct} />}
          <UserBadge initials={initials} name={user?.name} role={t(`auth:selectBusiness.role.${business.role}`)} onClick={() => navigate('/perfil')} />
          <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={logout}>{t('common:actions.logout')}</button>
        </>
      }
    >
      {pendingGrant && (
        <div style={{ background: 'var(--color-warn-soft)', borderRadius: 'var(--radius-md)', padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span><strong>{t('nav:supportRequest', { name: pendingGrant.superAdminName })}</strong> <span className="k-muted">{pendingGrant.reason} · {pendingGrant.scope.map((s) => t(`nav:supportScopes.${s}`)).join(', ')} · {pendingGrant.requestedDurationMin} min</span></span>
          <span style={{ display: 'flex', gap: 8 }}><button type="button" className="k-btn k-btn--sm" onClick={() => decide.mutate({ id: pendingGrant.id, approve: false })}>{t('nav:supportDeny')}</button><button type="button" className="k-btn k-btn--sm k-btn--primary" onClick={() => decide.mutate({ id: pendingGrant.id, approve: true })}>{t('nav:supportApprove')}</button></span>
        </div>
      )}
      {isAdmin && wizard?.status === 'AVAILABLE' && !location.pathname.startsWith('/ativacao') && (
        <div style={{ background: 'var(--color-accent-soft, rgba(47, 107, 95, .08))', borderRadius: 'var(--radius-md)', padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span><strong>{t('nav:wizardBanner')}</strong> <span className="k-muted">{t('nav:wizardBannerHint')}</span></span>
          <NavLink to="/ativacao" className="k-btn k-btn--sm k-btn--primary">{t('nav:wizardBannerLink')}</NavLink>
        </div>
      )}
      {(full.data?.status === 'QUOTA_EXHAUSTED' || full.data?.status === 'PAUSED') && !location.pathname.startsWith('/config/plano') && (
        <div style={{ background: 'var(--color-danger, #9b3b2e)', color: '#fff', borderRadius: 'var(--radius-md)', padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <strong>{t('nav:paused')}</strong>
          <NavLink to="/config/plano" style={{ color: '#fff', fontWeight: 600 }}>{t('nav:pausedLink')}</NavLink>
        </div>
      )}
      <Outlet />
      <HelpButton wizardOpen={isAdmin && (wizard?.status === 'AVAILABLE' || new URLSearchParams(location.search).get('assistente') === '1')} />
    </AppShell>
  );
}
